import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import COLORS from 'constants/colors';

import BadgeEditorColorInput from './BadgeEditorColorInput';
import BadgeEditorIconList from './BadgeEditorIconList';
import BadgeEditorPreview from './BadgeEditorPreview';

const VALID_HEX_COLOR_REGEX = /^#(?:[0-9a-f]{6})$/i;

const isValidHexColor = color => VALID_HEX_COLOR_REGEX.test(color);

function createErrors(state) {
  const errors = {};
  if (!isValidHexColor(state.foregroundColor)) {
    errors.foregroundColor = t(
      'certifications.badge_editor.errors.foreground_color_invalid',
    );
  }

  if (!isValidHexColor(state.backgroundColor)) {
    errors.backgroundColor = t(
      'certifications.badge_editor.errors.background_color_invalid',
    );
  }

  return errors;
}

function isDirtyBadge(initialState, state) {
  return ['iconName', 'foregroundColor', 'backgroundColor'].some(
    key => initialState[key] !== state[key],
  );
}

const selectSafeIconColor =
  state =>
  ({ colorType, iconName }) => {
    if (colorType && state.iconName === iconName) {
      return !state.errors[colorType]
        ? state[colorType]
        : state.initialBadge[colorType];
    }

    return colorType === 'foregroundColor' ? COLORS.NIGHT : '#ffffff';
  };

const CHANGE_COLOR = 'CHANGE_COLOR';
const CHANGE_ICON = 'CHANGE_ICON';

function badgeReducer(state, action) {
  switch (action.type) {
    case CHANGE_ICON: {
      return {
        ...state,
        iconName: action.iconName,
        isDirty: action.isDirty,
      };
    }

    case CHANGE_COLOR: {
      return {
        ...state,
        [action.colorType]: action.color,
        isValid: action.isValid,
        errors: action.errors,
        isDirty: action.isDirty,
      };
    }

    default:
      return state;
  }
}

const BadgeEditor = props => {
  const [state, dispatch] = useReducer(badgeReducer, {
    initialBadge: {
      iconName: props.iconName,
      foregroundColor: props.foregroundColor,
      backgroundColor: props.backgroundColor,
    },
    iconName: props.iconName,
    foregroundColor: props.foregroundColor,
    backgroundColor: props.backgroundColor,
    errors: {},
    isValid: true,
    isDirty: false,
  });

  const onIconChange = iconName => {
    const newState = { ...state, iconName };
    const isDirty = isDirtyBadge(state.initialBadge, newState);

    dispatch({ type: CHANGE_ICON, iconName, isDirty });

    if (props.onChange) {
      props.onChange({ ...state, iconName, isDirty });
    }
  };

  const onColorChange = ({ target: { value, name } }) => {
    const newState = { ...state, [name]: value };
    const errors = createErrors(newState);
    const isValid = isEmpty(errors);
    const isDirty = isDirtyBadge(state.initialBadge, newState);

    dispatch({
      type: CHANGE_COLOR,
      colorType: name,
      color: value,
      isValid,
      isDirty,
      errors,
    });

    if (props.onChange) {
      props.onChange({ ...state, [name]: value, isValid, isDirty, errors });
    }
  };

  return (
    <div className="d-f fd-c">
      <BadgeEditorIconList
        disabled={props.disabled}
        onChange={onIconChange}
        selectSafeIconColor={selectSafeIconColor(state)}
      />
      <div className="d-f">
        <div className="f-1 d-f ai-fs fd-c jc-sb">
          <BadgeEditorColorInput
            disabled={props.disabled}
            title={t('certifications.badge_editor.foreground_color')}
            value={state.foregroundColor}
            name="foregroundColor"
            placeholder="#ffffff"
            onChange={onColorChange}
            error={state.errors.foregroundColor}
          />
          <BadgeEditorColorInput
            disabled={props.disabled}
            title={t('certifications.badge_editor.background_color')}
            value={state.backgroundColor}
            name="backgroundColor"
            placeholder="#f9cf36"
            onChange={onColorChange}
            error={state.errors.backgroundColor}
          />
        </div>
        <BadgeEditorPreview
          iconName={state.iconName}
          foregroundColor={state.foregroundColor}
          backgroundColor={state.backgroundColor}
        />
      </div>
    </div>
  );
};

BadgeEditor.propTypes = {
  disabled: PropTypes.bool,
  iconName: PropTypes.string.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  foregroundColor: PropTypes.string.isRequired,
  onChange: PropTypes.func,
};

export default BadgeEditor;
